import React, { useEffect, useState } from "react";
import targetApi from "../../services/targetApi";
import DataTable from "../../components/common/DataTable";
import KpiCard from "../../components/target/KpiCard";
import { useToast } from "../../components/common/Toast";
import { FaPlus, FaEdit, FaTrash, FaBullseye, FaCheckCircle, FaHourglassHalf, FaTimesCircle } from "react-icons/fa";
import "../../styles/target.css";

const TARGET_TYPES = ["Money", "Calls", "Sales", "Meetings", "Leads", "Revenue", "Other"];
const STATUSES = ["Active", "Completed", "Expired"];

const emptyForm = {
  id: null,
  user_id: "",
  title: "",
  target_type: "Money",
  total_target: "",
  start_date: "",
  end_date: "",
  status: "Active",
};

/* =========================================================
   Admin: Target Management
   Create / edit / delete targets assigned to employees.
   Endpoints:
     POST   /api/targets/create
     GET    /api/targets/manage
     GET    /api/targets/manage/:id
     PUT    /api/targets/manage/:id
     DELETE /api/targets/manage/:id
     GET    /api/targets/dashboard/stats
========================================================= */
function TargetManagement() {
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const limit = 10;

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [targetType, setTargetType] = useState("");

  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    targetApi.getAdminTargetStats().then((res) => setStats(res.data)).catch(() => {});
    targetApi.getTargetableUsers().then((res) => setUsers(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, status, targetType]);

  const fetchList = () => {
    setLoading(true);
    targetApi
      .listTargets({
        search: search || undefined,
        status: status || undefined,
        target_type: targetType || undefined,
        page,
        limit,
      })
      .then((res) => {
        setRows(res.data.data || []);
        setTotal(res.data.total || 0);
      })
      .catch(() => toast.error("Failed to load targets"))
      .finally(() => setLoading(false));
  };

  const openCreate = () => {
    setForm(emptyForm);
    setEditing(false);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      user_id: row.user_id,
      title: row.title,
      target_type: row.target_type,
      total_target: row.total_target,
      start_date: row.start_date ? row.start_date.slice(0, 10) : "",
      end_date: row.end_date ? row.end_date.slice(0, 10) : "",
      status: row.status,
    });
    setEditing(true);
    setShowModal(true);
  };

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSave = (e) => {
    e.preventDefault();

    if (!form.title || !form.target_type || form.total_target === "" || !form.start_date || !form.end_date) {
      toast.error("Please fill all required fields");
      return;
    }
    if (!editing && !form.user_id) {
      toast.error("Please select an employee");
      return;
    }

    setSaving(true);
    const payload = {
      title: form.title,
      target_type: form.target_type,
      total_target: Number(form.total_target),
      start_date: form.start_date,
      end_date: form.end_date,
    };

    const action = editing
      ? targetApi.updateTarget(form.id, { ...payload, status: form.status })
      : targetApi.createTarget({ ...payload, user_id: Number(form.user_id) });

    action
      .then(() => {
        toast.success(editing ? "Target updated successfully" : "Target created successfully");
        setShowModal(false);
        fetchList();
        targetApi.getAdminTargetStats().then((res) => setStats(res.data)).catch(() => {});
      })
      .catch((err) => toast.error(err?.response?.data?.message || "Failed to save target"))
      .finally(() => setSaving(false));
  };

  const handleDelete = (row) => {
    if (!window.confirm(`Delete target "${row.title}"? This cannot be undone.`)) return;
    targetApi
      .deleteTarget(row.id)
      .then(() => {
        toast.success("Target deleted");
        fetchList();
      })
      .catch(() => toast.error("Failed to delete target"));
  };

  const columns = [
    { key: "employee_name", label: "Employee", sortable: false },
    { key: "title", label: "Title", sortable: false },
    { key: "target_type", label: "Type", sortable: false },
    {
      key: "progress",
      label: "Progress",
      render: (r) => (
        <div className="mini-progress-cell">
          <div className="mini-progress-track">
            <div
              className="mini-progress-fill"
              style={{ width: `${Math.min(r.progress_percent || 0, 100)}%` }}
            />
          </div>
          <span>
            {r.completed_target}/{r.total_target} ({r.progress_percent}%)
          </span>
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (r) => (
        <span className={`status-pill status-${(r.status || "").toLowerCase()}`}>{r.status}</span>
      ),
    },
    {
      key: "end_date",
      label: "Ends",
      render: (r) => (r.end_date ? new Date(r.end_date).toLocaleDateString("en-IN") : "-"),
    },
    {
      key: "actions",
      label: "Actions",
      render: (r) => (
        <div className="row-actions">
          <button className="icon-btn" onClick={() => openEdit(r)} title="Edit">
            <FaEdit />
          </button>
          <button className="icon-btn danger" onClick={() => handleDelete(r)} title="Delete">
            <FaTrash />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="target-management-page">
      <div className="mytarget-header">
        <div>
          <h2>Target Management</h2>
          <p>Assign, track and manage employee targets</p>
        </div>
        <button className="btn-primary" onClick={openCreate}>
          <FaPlus /> New Target
        </button>
      </div>

      {stats && (
        <div className="kpi-mini-grid">
          <KpiCard label="Total Targets" value={stats.total_targets || 0} icon={<FaBullseye />} accent="indigo" />
          <KpiCard label="Active" value={stats.active_targets || 0} icon={<FaHourglassHalf />} accent="amber" />
          <KpiCard label="Completed" value={stats.completed_targets || 0} icon={<FaCheckCircle />} accent="green" />
          <KpiCard label="Expired" value={stats.expired_targets || 0} icon={<FaTimesCircle />} accent="red" />
        </div>
      )}

      <div className="filters-bar">
        <input
          placeholder="Search by employee or title..."
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />
        <select
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value);
          }}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={targetType}
          onChange={(e) => {
            setPage(1);
            setTargetType(e.target.value);
          }}
        >
          <option value="">All types</option>
          {TARGET_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        emptyIcon="🎯"
        emptyText="No targets found"
        page={page}
        limit={limit}
        total={total}
        onPageChange={setPage}
      />

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h3>{editing ? "Edit Target" : "Create Target"}</h3>
            <form onSubmit={handleSave}>
              {!editing && (
                <div className="form-row">
                  <label>Employee</label>
                  <select name="user_id" value={form.user_id} onChange={handleChange}>
                    <option value="">Select employee</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name} ({u.role})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-row">
                <label>Title</label>
                <input name="title" value={form.title} onChange={handleChange} placeholder="e.g. April Sales Target" />
              </div>

              <div className="form-row-group">
                <div className="form-row">
                  <label>Target Type</label>
                  <select name="target_type" value={form.target_type} onChange={handleChange}>
                    {TARGET_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-row">
                  <label>Total Target</label>
                  <input
                    type="number"
                    min="1"
                    name="total_target"
                    value={form.total_target}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row-group">
                <div className="form-row">
                  <label>Start Date</label>
                  <input type="date" name="start_date" value={form.start_date} onChange={handleChange} />
                </div>
                <div className="form-row">
                  <label>End Date</label>
                  <input type="date" name="end_date" value={form.end_date} onChange={handleChange} />
                </div>
              </div>

              {editing && (
                <div className="form-row">
                  <label>Status</label>
                  <select name="status" value={form.status} onChange={handleChange}>
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Saving..." : editing ? "Save Changes" : "Create Target"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TargetManagement;
